import { Fragment, useEffect, useMemo } from 'react'
import { Divider, Grid } from 'semantic-ui-react'

import { Button, Icon, Label, Modal, Text } from '@/componentsV1/BaseComponents'
import { AnnualTaxFilingForm } from '@/features/taxes/annual-taxes/annualTaxFilingForms.slice'
import { fetchAdminAllEoyReviewStepsIfNeeded } from '@/features/taxes/annual-taxes/tax-checklist/shared/review-steps-and-progresses/allEoyReviewSteps.slice'
import { useFetchResponse, useReselector } from '@/utils/sharedHooks'
import {
  fetchUserEoyReviewForUser,
  UserEoyReviewProgress,
} from '@/features/taxes/annual-taxes/tax-checklist/shared/review-steps-and-progresses/userEndOfYearReviewProgress.slice'
import {
  getProgressFromIdentifier,
  getTaxChecklistSections,
  selectPersonalFilingIncludedForFree,
} from '@/features/taxes/annual-taxes/tax-checklist/taxChecklist.selectors'
import { getAllEoyReviewSteps } from '@/features/taxes/annual-taxes/tax-checklist/shared/review-steps-and-progresses/endOfYearReviewSteps.selector'
import { DATE_FORMATS_LUXON, formatISOFromUTC } from '@/utils/dateHelpers'
import { faCheck } from '@fortawesome/pro-regular-svg-icons'
import { useAppDispatch } from '@/utils/typeHelpers'

const getLabel = (progress: UserEoyReviewProgress | undefined) => {
  if (!progress) {
    return <Label color="gray">Not Started</Label>
  } else if (progress.completedAt) {
    return (
      <Label color="green">
        <Icon icon={faCheck} style={{ marginRight: 10 }} />
        Completed
      </Label>
    )
  }

  return <Label color="yellow">In Progress</Label>
}

const FormProgressModal = ({
  form,
  forms,
  onClose,
}: {
  form: AnnualTaxFilingForm
  forms: AnnualTaxFilingForm[]
  onClose: () => void
}) => {
  const dispatch = useAppDispatch()
  const steps = useReselector(getAllEoyReviewSteps)

  const progresses = useFetchResponse(
    fetchUserEoyReviewForUser,
    form.year,
    form.userId
  )

  const subscriptionIncludesFreePersonalFiling = useReselector(
    selectPersonalFilingIncludedForFree
  )

  const checklistSections = useMemo(
    () =>
      getTaxChecklistSections(
        subscriptionIncludesFreePersonalFiling,
        forms,
        form.id
      ),
    [form.id, forms, subscriptionIncludesFreePersonalFiling]
  )

  useEffect(() => {
    dispatch(fetchAdminAllEoyReviewStepsIfNeeded())
  }, [dispatch])

  return (
    <Modal open onClose={onClose} closeIcon>
      <Modal.Header>{form.formType?.displayName} Form Progress</Modal.Header>
      <Modal.Content>
        <Grid>
          {checklistSections.map((sections, i) => (
            <Fragment key={sections[0]}>
              {i !== 0 && <Divider />}
              {sections.map((section) => {
                const { progress } = getProgressFromIdentifier(
                  section,
                  steps,
                  progresses || {}
                )
                const date = progress?.completedAt || progress?.updatedAt

                return (
                  <Grid.Row key={section} className="short">
                    <Grid.Column width={10}>
                      <Text>{section.replaceAll('-', ' ')}</Text>
                    </Grid.Column>
                    <Grid.Column width={3}>
                      <Text>
                        {date
                          ? formatISOFromUTC(
                              date,
                              DATE_FORMATS_LUXON.DISPLAY_SHORT
                            )
                          : '-'}
                      </Text>
                    </Grid.Column>
                    <Grid.Column width={3}>{getLabel(progress)}</Grid.Column>
                  </Grid.Row>
                )
              })}
            </Fragment>
          ))}
        </Grid>
      </Modal.Content>
      <Modal.Actions>
        <Button onClick={onClose}>Close</Button>
      </Modal.Actions>
    </Modal>
  )
}

export default FormProgressModal
